// components/canvas/canvas-toolbar.js — CANVAS EDITOR TOOLBAR
// ═══════════════════════════════════════════════════════════
// Toolbar for canvas focus mode: undo, redo, duplicate, rename.
// Renders as a thin bar below the canvas header.
//
// Usage:
//   <CanvasToolbar
//     onUndo={fn}    onRedo={fn}
//     canUndo={bool} canRedo={bool}
//     onDuplicate={fn}
//     onRename={fn}
//     title={string}
//     readOnly={bool}
//   />
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';

function ToolbarButton({ icon, label, onClick, disabled, danger }) {
  const [hovered, setHovered] = useState(false);
  const color = disabled ? '#333' : hovered ? (danger ? '#c46050' : '#c8a96e') : '#555';
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={label}
      disabled={disabled}
      style={{
        background: 'none',
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        color,
        fontSize: 10,
        fontFamily: 'Georgia, serif',
        padding: '3px 8px',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        transition: 'color 0.12s',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <span style={{ fontSize: 12 }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

export default function CanvasToolbar({
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onDuplicate,
  onRename,
  onHistory,
  onExport,
  exportOpen,
  title,
  readOnly = false,
}) {
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(title || '');

  function handleRenameSubmit() {
    if (renameValue.trim() && renameValue !== title) {
      onRename?.(renameValue.trim());
    }
    setRenaming(false);
  }

  return (
    <div style={{
      height: 32,
      borderBottom: '1px solid #1e1e2a',
      background: '#0b0b0b',
      display: 'flex',
      alignItems: 'center',
      padding: '0 10px',
      gap: 2,
      flexShrink: 0,
    }}>
      {/* Undo / Redo */}
      {!readOnly && (
        <>
          <ToolbarButton icon="↩" label="Undo" onClick={onUndo} disabled={!canUndo} />
          <ToolbarButton icon="↪" label="Redo" onClick={onRedo} disabled={!canRedo} />
          <div style={{ width: 1, height: 16, background: '#1e1e2a', margin: '0 4px' }} />
        </>
      )}

      {/* Duplicate */}
      {onDuplicate && (
        <ToolbarButton icon="⊡" label="Duplicate" onClick={onDuplicate} />
      )}

      {/* Rename — inline edit */}
      {onRename && (
        renaming ? (
          <input
            autoFocus
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={e => {
              if (e.key === 'Enter') handleRenameSubmit();
              if (e.key === 'Escape') setRenaming(false);
            }}
            style={{
              background: '#141414',
              border: '1px solid rgba(200,169,110,0.3)',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: 10,
              fontFamily: 'Georgia, serif',
              color: '#d4d4d4',
              outline: 'none',
              width: 160,
            }}
          />
        ) : (
          <ToolbarButton icon="✎" label="Rename" onClick={() => { setRenameValue(title || ''); setRenaming(true); }} />
        )
      )}

      {/* Version history */}
      {onHistory && (
        <ToolbarButton icon="⟲" label="History" onClick={onHistory} />
      )}

      <div style={{ width: 1, height: 16, background: '#1e1e2a', margin: '0 4px' }} />

      {/* Export */}
      {onExport && (
        <div style={{ position: 'relative' }}>
          <ToolbarButton icon="↗" label="Export" onClick={onExport} />
        </div>
      )}

      <div style={{ flex: 1 }} />

      {/* Read-only indicator */}
      {readOnly && (
        <span style={{
          fontSize: 9, color: '#333', fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
        }}>
          Read only
        </span>
      )}
    </div>
  );
}
